
import { Lesson, lessons as defaultLessons } from "@/data/lessons";
import analytics from '@/utils/analytics';

export const getAllLessons = (): Lesson[] => {
  try {
    const savedLessons = localStorage.getItem('hormony_lessons');
    if (savedLessons) {
      return JSON.parse(savedLessons);
    }
    return defaultLessons;
  } catch (error) {
    console.error("Error loading lessons:", error);
    return defaultLessons;
  }
};

export const getLessonByDay = (day: number): Lesson | undefined => {
  const allLessons = getAllLessons();
  return allLessons.find(lesson => lesson.day === day);
};

export const saveLesson = (lesson: Lesson): void => {
  try {
    const allLessons = getAllLessons();
    let updatedLessons: Lesson[];
    
    // Check if the lesson already exists
    const existingIndex = allLessons.findIndex(l => l.day === lesson.day);
    
    if (existingIndex >= 0) {
      // Update existing lesson
      updatedLessons = [...allLessons];
      updatedLessons[existingIndex] = lesson;
    } else {
      // Add new lesson
      updatedLessons = [...allLessons, lesson];
    }
    
    // Sort by day number
    updatedLessons.sort((a, b) => a.day - b.day);
    
    // Save to localStorage
    localStorage.setItem('hormony_lessons', JSON.stringify(updatedLessons));
    
    // Track this action
    analytics.trackAdminAction('save_lesson', { day: lesson.day, title: lesson.title });
  } catch (error) {
    console.error("Error saving lesson:", error);
    throw error;
  }
};

export const exportLessons = (): string => {
  try {
    const allLessons = getAllLessons();
    return JSON.stringify(allLessons, null, 2);
  } catch (error) {
    console.error("Error exporting lessons:", error);
    throw error;
  }
};

export const importLessons = (jsonData: string): void => {
  try {
    const lessons = JSON.parse(jsonData);
    
    // Validate that it's an array of lessons
    if (!Array.isArray(lessons)) {
      throw new Error("Invalid lesson data format");
    }
    
    // Save to localStorage
    localStorage.setItem('hormony_lessons', jsonData);
    
    // Track this action
    analytics.trackAdminAction('import_lessons', { count: lessons.length });
  } catch (error) {
    console.error("Error importing lessons:", error);
    throw error;
  }
};

// Symptoms management
const SYMPTOM_STORAGE_KEY = 'hormony_symptom_options';

export const getSymptomOptions = (): string[] => {
  try {
    const savedSymptoms = localStorage.getItem(SYMPTOM_STORAGE_KEY);
    if (savedSymptoms) {
      return JSON.parse(savedSymptoms);
    }
    
    // Default symptoms
    return [
      "Fatigue", "Headache", "Bloating", "Cramps", 
      "Mood Swings", "Insomnia", "Low Energy", "Cravings"
    ];
  } catch (error) {
    console.error("Error loading symptom options:", error);
    return [
      "Fatigue", "Headache", "Bloating", "Cramps", 
      "Mood Swings", "Insomnia", "Low Energy", "Cravings"
    ];
  }
};

export const saveSymptomOptions = (symptoms: string[]): void => {
  try {
    localStorage.setItem(SYMPTOM_STORAGE_KEY, JSON.stringify(symptoms));
    
    // Track this action
    analytics.trackAdminAction('save_symptom_options', { count: symptoms.length });
  } catch (error) {
    console.error("Error saving symptom options:", error);
    throw error;
  }
};

// For tracking user-reported symptoms
export const saveUserSymptoms = (day: number, symptoms: string[]): void => {
  try {
    // In a real app, this would send data to a backend database
    // For now, store locally and track with analytics
    const userSymptomsKey = `hormony_user_symptoms_day_${day}`;
    localStorage.setItem(userSymptomsKey, JSON.stringify(symptoms));
    
    // Track symptoms with analytics
    analytics.trackSymptoms(day, symptoms);
  } catch (error) {
    console.error("Error saving user symptoms:", error);
  }
};
